const renderRGB = (rgb, mask, roofOnly) => {
  if (!rgb || !rgb.width || !rgb.height) {
    console.error("⚠️ Erreur: Données RGB invalides", rgb);
    return null;
  }
  if (roofOnly && (!mask || !mask.width || !mask.height)) {
    console.error("⚠️ Erreur: Données mask invalides en roofOnly", mask);
    return null;
  }

  const canvas = document.createElement("canvas");
  canvas.width = mask ? mask.width : rgb.width;
  canvas.height = mask ? mask.height : rgb.height;

  const dw = rgb.width / canvas.width;
  const dh = rgb.height / canvas.height;

  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(canvas.width, canvas.height);

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const rgbIdx = Math.floor(y * dh) * rgb.width + Math.floor(x * dw);
      const maskIdx = y * canvas.width + x;
      const imgIdx = y * canvas.width * 4 + x * 4;

      img.data[imgIdx + 0] = rgb.rasters[0][rgbIdx];
      img.data[imgIdx + 1] = rgb.rasters[1][rgbIdx];
      img.data[imgIdx + 2] = rgb.rasters[2][rgbIdx];
      img.data[imgIdx + 3] = roofOnly
        ? mask.rasters[0][maskIdx] > 0
          ? 255
          : 0
        : 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  return canvas;
};

const clamp = (x, min, max) => Math.min(Math.max(x, min), max);

const normalize = (x, max = 1, min = 0) => {
  const y = (x - min) / (max - min);
  return clamp(y, 0, 1);
};

const lerp = (x, y, t) => x + t * (y - x);

const colorToRGB = (color) => {
  const hex = color.startsWith("#") ? color.slice(1) : color;
  return {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
  };
};

const createPalette = (hexColors) => {
  const rgb = hexColors.map(colorToRGB);
  const size = 256;
  const step = (rgb.length - 1) / (size - 1);
  return Array(size)
    .fill(0)
    .map((_, i) => {
      const index = i * step;
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      return {
        r: lerp(rgb[lower].r, rgb[upper].r, index - lower),
        g: lerp(rgb[lower].g, rgb[upper].g, index - lower),
        b: lerp(rgb[lower].b, rgb[upper].b, index - lower),
      };
    });
};

const renderPalette = ({ data, mask, colors, min, max, index, roofOnly }) => {
  const palette = createPalette(colors ?? ["000000", "ffffff"]);
  const indices = data.rasters[index ?? 0]
    .map((x) => normalize(x, max ?? 1, min ?? 0))
    .map((x) => Math.round(x * (palette.length - 1)));

  return renderRGB(
    {
      ...data,
      rasters: [
        indices.map((i) => palette[i].r),
        indices.map((i) => palette[i].g),
        indices.map((i) => palette[i].b),
      ],
    },
    roofOnly ? mask : null,
    roofOnly,
  );
};

// Converts lat/lng coordinates into pixels in the canvas, according to bounds and dimensions
const latLngToPixel = (lat, lng, bounds, width, height) => {
  const x = ((lng - bounds.west) / (bounds.east - bounds.west)) * width;
  const y = ((bounds.north - lat) / (bounds.north - bounds.south)) * height;
  return { x: Math.floor(x), y: Math.floor(y) };
};

// Trim a canvas (generated image) to keep only the area corresponding to the roof segments
const cropMaskCanvas = (canvas, segmentBounds, maskBounds) => {
  const fullWidth = canvas.width;
  const fullHeight = canvas.height;

  const topLeft = latLngToPixel(
    segmentBounds.north,
    segmentBounds.west,
    maskBounds,
    fullWidth,
    fullHeight,
  );
  const bottomRight = latLngToPixel(
    segmentBounds.south,
    segmentBounds.east,
    maskBounds,
    fullWidth,
    fullHeight,
  );

  const cropWidth = bottomRight.x - topLeft.x;
  const cropHeight = bottomRight.y - topLeft.y;

  const croppedCanvas = document.createElement("canvas");
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;

  const ctx = croppedCanvas.getContext("2d");
  ctx.drawImage(
    canvas,
    topLeft.x,
    topLeft.y,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  );

  return croppedCanvas;
};

// Apply a filter to maskData to retain only the pixels inside the segments' bounding box
const filterMaskByBoundingBox = (maskData, boundsFilter) => {
  const { width, height, rasters, bounds } = maskData;
  const original = rasters[0];
  const filtered = new Array(original.length).fill(0);

  const topLeft = latLngToPixel(
    boundsFilter.north,
    boundsFilter.west,
    bounds,
    width,
    height,
  );
  const bottomRight = latLngToPixel(
    boundsFilter.south,
    boundsFilter.east,
    bounds,
    width,
    height,
  );

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (
        x >= topLeft.x &&
        x <= bottomRight.x &&
        y >= topLeft.y &&
        y <= bottomRight.y
      ) {
        filtered[i] = original[i];
      }
    }
  }

  return {
    ...maskData,
    rasters: [filtered],
  };
};

export const solarColors = [
  "#333333",
  "#666666",
  "#FFD700",
  "#FFA500",
  "#FF4500",
];

export const generateOverlay = (
  tiffData,
  maskData,
  roofOnly,
  segmentBoundingBox = null,
) => {
  if (!tiffData) return null;

  const rasterArray = tiffData.rasters[0];
  const min = Math.min(...rasterArray);
  const max = Math.max(...rasterArray);

  const filteredMask =
    roofOnly && segmentBoundingBox
      ? filterMaskByBoundingBox(maskData, segmentBoundingBox)
      : maskData;

  const fullCanvas = renderPalette({
    data: tiffData,
    mask: roofOnly ? filteredMask : null,
    colors: solarColors,
    min,
    max,
    index: 0,
    roofOnly,
  });

  return roofOnly && segmentBoundingBox
    ? cropMaskCanvas(fullCanvas, segmentBoundingBox, maskData.bounds)
    : fullCanvas;
};

export const createLatLngBounds = (maps, swLat, swLng, neLat, neLng) => {
  return new maps.LatLngBounds(
    new maps.LatLng(swLat, swLng),
    new maps.LatLng(neLat, neLng),
  );
};

export const addSolarOverlay = (map, maps, canvas, bounds) => {
  if (!map || !maps || !canvas || !bounds) {
    console.error("Paramètres invalides pour addSolarOverlay.");
    return null;
  }

  const imageUrl = canvas.toDataURL("image/webp");

  const overlay = new maps.GroundOverlay(imageUrl, bounds);

  overlay.setMap(map);

  return overlay;
};
